/*
  The Overview's period control and the dashboard Sales card's ranges (ALS-85), one place each.
  The values are the API's enum names; the labels are the prototype's.
*/

export const REPORT_PERIOD = {
    Day: 'Day',
    Week: 'Week',
    Month: 'Month',
    Quarter: 'Quarter',
    Year: 'Year',
} as const;

export type ReportPeriod = (typeof REPORT_PERIOD)[keyof typeof REPORT_PERIOD];

export const REPORT_PERIODS = Object.values(REPORT_PERIOD) as [ReportPeriod, ...ReportPeriod[]];

/**
 * Tax reconciliation's periods (ALS-90): month, quarter and year, the cycles VAT is filed on. The
 * prototype offers nothing shorter, since nobody files a daily or weekly return.
 */
export const TAX_PERIODS = [
    REPORT_PERIOD.Month,
    REPORT_PERIOD.Quarter,
    REPORT_PERIOD.Year,
] as const satisfies readonly ReportPeriod[];

export type TaxPeriod = (typeof TAX_PERIODS)[number];

/** The shared period as the Tax view reads it: a Day or Week chosen on another tab reads as Month. */
export function taxPeriod(period: ReportPeriod): TaxPeriod {
    return (TAX_PERIODS as readonly ReportPeriod[]).includes(period)
        ? (period as TaxPeriod)
        : REPORT_PERIOD.Month;
}

/** What each period's chart buckets are, as the card's subtitle says it (data-reports.js `axis`). */
export const PERIOD_AXIS: Record<ReportPeriod, string> = {
    Day: 'by day · last 7 days',
    Week: 'by week · last 6 weeks',
    Month: 'by month · last 6 months',
    Quarter: 'by quarter · last 6 quarters',
    Year: 'by year · last 4 years',
};

export const DASHBOARD_RANGE = {
    SevenDays: 'SevenDays',
    FourWeeks: 'FourWeeks',
    MonthToDate: 'MonthToDate',
    YearToDate: 'YearToDate',
    OneYear: 'OneYear',
    Custom: 'Custom',
} as const;

export type DashboardRange = (typeof DASHBOARD_RANGE)[keyof typeof DASHBOARD_RANGE];

/** The Sales card's tabs, in the order screens/dashboard.html draws them. */
export const DASHBOARD_RANGE_LABEL: Record<DashboardRange, string> = {
    SevenDays: '7D',
    FourWeeks: '4W',
    MonthToDate: 'MTD',
    YearToDate: 'YTD',
    OneYear: '1Y',
    Custom: 'Custom',
};
